import React from 'react';

import { NotFoundPage } from './components/not-found/NotFoundPage';
import {
  HomePage,
  LegacyManagementRedirect,
  ManagementIndexRedirect,
  RequiredAuthenticatedRoutes,
  RequiredNewSignupRoutes,
  RequiredUserAdminRoutes,
  RequiredUserApproverRoutes,
  RequiredUserRootRoutes,
} from './helpers/routerHelpers';
import { logger } from './initialisers/logger';
import { Routes, Scope } from './lib/routes';
import Logout from './pages/sessions/delete/Logout';
import TripPdfPreview from './pdf/trips/TripPreview';

export const routes = new Routes(logger);

/** Layouts */

/** Reporting */
const ReportingPage = React.lazy(() => import('./pages/reporting/index/ReportingPage'));
const ReportingAgentPage = React.lazy(() => import('./pages/reporting/agent/ReportingAgentPage'));

/** Agents (beta) */
const AgentsPage = React.lazy(() => import('./pages/agents/index/AgentsPage'));

/** AI Agent */
const AiAgentPage = React.lazy(() => import('./pages/aiAgent/index/AiAgentPage'));
const TravelAgentConversationPage = React.lazy(() => import('./pages/aiAgent/show/TravelAgentConversationPage'));

/** My account */
const EditAccountPage = React.lazy(() => import('./pages/my-account/edit/EditAccountPage'));
const OverviewPage = React.lazy(() => import('./pages/my-account/index/MyAccountPage'));

/** Preferences / Personalise */
const PreferencesPage = React.lazy(() => import('./pages/preferences/PreferencesPage'));

/** Authentication — AuthKit owns the credential flows; these two SPA routes are
 * the redirect into the hosted UI and the post-callback router. */
const LoginPage = React.lazy(() => import('./pages/sessions/new/LoginPage'));
const AuthCallbackPage = React.lazy(() => import('./pages/sessions/callback/AuthCallbackPage'));

/** Link accounts */
const AccountLinkPage = React.lazy(() => import('./pages/link-accounts/index/AccountLinkPage'));
const AccountLinkedPage = React.lazy(() => import('./pages/link-accounts/show/AccountLinkedPage'));

/** External property view (used by Slack, Teams, and other integrations) */
const ExternalPropertyPage = React.lazy(() => import('./pages/external/PropertyPage'));

/** External hotel-map view — all agent-found hotels as pins (Slack "View all hotels") */
const ExternalMapPage = React.lazy(() => import('./pages/external/AgentMapPage'));

/** External seat picker — the cabin Slack cannot draw (Slack "Open seat map") */
const ExternalSeatsPage = React.lazy(() => import('./pages/external/AgentSeatsPage'));

/** Act as a company, the landing of "Open company" */
const ActAsLandingPage = React.lazy(() => import('./pages/act-as/ActAsLandingPage'));

/** trips */
const TripsPage = React.lazy(() => import('./pages/trips/index/TripsPage'));
const TripPage = React.lazy(() => import('./pages/trips/show/TripPage'));

/** Manual booking */
const BasketPage = React.lazy(() => import('./pages/manual-booking/BasketPage'));
const ManualBookingLayout = React.lazy(() => import('./pages/manual-booking/ManualBookingLayout'));
const SearchPage = React.lazy(() => import('./pages/manual-booking/search/index/SearchPage'));

/** Onboarding */
const OnboardInvitedUser = React.lazy(() => import('./pages/onbarding/onboard-invited-user/OnboardInvitedUser'));
const OnboardRegister = React.lazy(() => import('./pages/onbarding/onboarding-register/OnboardRegister'));

/**
 * Management
 */

/** Users page  */
const UsersPage = React.lazy(() => import('./pages/settings/users/index/UsersPage'));

/** Companies page */
const EditCompanyPage = React.lazy(() => import('./pages/settings/companies/edit/EditCompanyPage'));

/** Policies page */
const PoliciesPage = React.lazy(() => import('./pages/settings/policies/index/PoliciesPages'));

/** Integrations page */
const IntegrationsPage = React.lazy(() => import('./pages/settings/integrations/index/IntegrationsPage'));

/** Approvals detail page — kept as a deep-link target from the approvals table inside All Bookings. */
const ApprovalDetailPage = React.lazy(() => import('./pages/settings/approvals/show/ApprovalDetailPage'));

/** All Bookings — admin-only company-wide view with embedded approvals. */
const AllBookingsPage = React.lazy(() => import('./pages/all-bookings/index/AllBookingsPage'));

/**
 * Management section (the CRM pages, served under /management)
 */
const ManagementShell = React.lazy(() => import('./pages/management/ManagementShell'));

/** Users page  */
const CrmUsersPage = React.lazy(() => import('./pages/management/users/index/CrmUsersPage'));
const CrmEditUserPage = React.lazy(() => import('./pages/management/users/edit/CrmEditUserPage'));

/** Companies page */
const CrmCompaniesPage = React.lazy(() => import('./pages/management/companies/index/CrmCompaniesPage'));
const CrmEditCompanyPage = React.lazy(() => import('./pages/management/companies/edit/CrmEditCompanyPage'));

/** Agent conversations page */
const CrmAgentConversationsPage = React.lazy(
  () => import('./pages/management/agentConversations/index/CrmAgentConversations'),
);
const CrmAgentConversationPage = React.lazy(
  () => import('./pages/management/agentConversations/show/CrmAgentConversation'),
);

/** Trips page */
const CrmTripsPage = React.lazy(() => import('./pages/management/trips/index/CrmTripsPage'));
const CrmCreateTripsPage = React.lazy(() => import('./pages/management/trips/new/CrmCreateTripsPage'));
const CrmEditTripsPage = React.lazy(() => import('./pages/management/trips/edit/CrmEditTripsPage'));

/**
 * Default route where it doesn't matter if the user is authenticated or not
 */
routes.scope({ name: 'home', path: '' }, (session: Scope) => {
  /**
   * This is be based if the user is authenticated or not. This is needed because of route nesting
   */
  session.action({
    type: 'index',
    alias: '/',
    element: <HomePage authElem={<AiAgentPage />} notAuthElem={<LoginPage />} />,
  });
});

/** The management section's URLs before it moved to `/management`, sent on to the same page there. */
routes.scope({ name: 'crm' }, (legacyScope: Scope) => {
  legacyScope.action({ type: 'default', element: <LegacyManagementRedirect /> });
});

routes.scope({ name: 'link-accounts' }, (linkAccountsScope: Scope) => {
  linkAccountsScope.action({ type: 'index', element: <AccountLinkPage /> });
  linkAccountsScope.action({ type: 'linked', element: <AccountLinkedPage /> });
});

routes.scope({ name: 'external' }, (externalScope: Scope) => {
  externalScope.scope({ name: 'property' }, (propertyScope: Scope) => {
    propertyScope.action({ type: 'show', element: <ExternalPropertyPage /> });
  });
  externalScope.scope({ name: 'map' }, (mapScope: Scope) => {
    mapScope.action({ type: 'show', element: <ExternalMapPage /> });
  });
  externalScope.scope({ name: 'seats' }, (seatsScope: Scope) => {
    seatsScope.action({ type: 'show', element: <ExternalSeatsPage /> });
  });
});

routes.scope({ name: 'sessions', path: '' }, (session: Scope) => {
  session.action({ type: 'new', alias: '/login', element: <LoginPage /> });
  // AuthKit redirects here after a successful sign-in; AuthCallbackPage decides
  // where to send the user (existing → dashboard, new → /onboarding/register).
  session.action({ type: 'callback', alias: '/auth/callback', element: <AuthCallbackPage /> });
});

// Nothing requires "no WorkOS session at all" any more: password reset and email confirmation
// moved to AuthKit's hosted UI, and a Slack user with no account is now invited through WorkOS
// rather than sent to a self-signup wizard. `RequiredNotAuthenticatedRoutes` stays available in
// `routerHelpers` for the next route that needs it.

/**
 * Routes accessible only to a user mid-self-signup: WorkOS-authenticated but no Altitude
 * account yet. The wizard flips them to "fully authenticated" on submit, after which
 * `RequiredNewSignupRoutes` redirects them away from this scope.
 */
routes.scope({ element: <RequiredNewSignupRoutes /> }, (newSignupScope: Scope) => {
  newSignupScope.scope({ name: 'onboarding' }, (onboardingScope: Scope) => {
    onboardingScope.action({ type: 'register', element: <OnboardRegister /> });
  });
});

/**
 * Routes when the user is authenticated
 */
routes.scope({ element: <RequiredAuthenticatedRoutes /> }, (authScope: Scope) => {
  // Profile-completion landing for users who finished AuthKit signup but whose Altitude
  // row is still in `Invited` state. `RequiredAuthenticatedRoutes` redirects them here
  // until the wizard flips them to `Active`. Lives inside the auth scope because by the
  // time the user reaches it they are signed in (AuthKit verified them); the only thing
  // missing is the application-side profile.
  authScope.scope({ name: 'onboarding' }, (onboardingScope: Scope) => {
    onboardingScope.action({ type: 'complete-profile', element: <OnboardInvitedUser /> });
  });

  authScope.scope({ name: 'reporting', path: 'reporting' }, (homeScope: Scope) => {
    homeScope.action({ type: 'index', element: <ReportingPage /> });
    homeScope.action({ type: 'agent', element: <ReportingAgentPage /> });
  });

  authScope.scope({ name: 'sessions', path: '' }, (session: Scope) => {
    session.action({ type: 'delete', alias: '/logout', element: <Logout /> });
  });

  authScope.scope({ name: 'my-account' }, (myAccountScope: Scope) => {
    myAccountScope.action({ type: 'index', element: <OverviewPage /> });
    myAccountScope.action({ type: 'edit', element: <EditAccountPage />, member: false, optionalExtraParams: ['tab'] });
  });

  authScope.scope({ name: 'personalise' }, (preferencesScope: Scope) => {
    preferencesScope.action({
      type: 'index',
      element: <PreferencesPage />,
      member: false,
      optionalExtraParams: ['tab'],
    });
  });

  authScope.scope({ name: 'agents' }, (agentsScope: Scope) => {
    agentsScope.action({ type: 'index', element: <AgentsPage /> });
  });

  authScope.scope({ name: 'travel-agent' }, (travelAgentScope: Scope) => {
    travelAgentScope.action({ type: 'index', element: <AiAgentPage /> });
    travelAgentScope.action({ type: 'show', element: <TravelAgentConversationPage /> });
  });

  authScope.scope({ name: 'act-as' }, (actAsScope: Scope) => {
    actAsScope.action({ type: 'show', param: 'companyId', element: <ActAsLandingPage /> });
  });

  authScope.scope({ name: 'trips' }, (tripsScope: Scope) => {
    tripsScope.action({ type: 'index', element: <TripsPage /> });
    tripsScope.action({ type: 'show', element: <TripPage /> });
  });

  authScope.scope({ name: 'manual-booking', element: <ManualBookingLayout /> }, (flightBookingScope: Scope) => {
    flightBookingScope.scope({ name: 'search' }, (searchScope: Scope) => {
      searchScope.action({ type: 'index', element: <SearchPage />, optionalExtraParams: ['tab'] });
    });
    flightBookingScope.action({ type: 'basket', element: <BasketPage /> });
  });

  authScope.action({ type: 'default', element: <NotFoundPage /> });
});

/**
 * Routes for users that can decide on approvals — the detail page is the deep-link
 * target from the approvals table embedded inside All Bookings. Standalone listing
 * was removed when the Approvals nav item folded into All Bookings.
 */
routes.scope({ element: <RequiredUserApproverRoutes /> }, (approverScope: Scope) => {
  approverScope.scope({ name: 'settings' }, (settingsScope: Scope) => {
    settingsScope.scope({ name: 'approvals' }, (approvalsScope: Scope) => {
      approvalsScope.action({ type: 'show', element: <ApprovalDetailPage /> });
    });
  });
});

/**
 * Routes for users that have admin access for their company
 */
routes.scope({ element: <RequiredUserAdminRoutes /> }, (adminScope: Scope) => {
  adminScope.scope({ name: 'all-bookings' }, (allBookingsScope: Scope) => {
    allBookingsScope.action({ type: 'index', element: <AllBookingsPage /> });
  });

  adminScope.scope({ name: 'settings' }, (settingsScope: Scope) => {
    settingsScope.scope({ name: 'users' }, (usersScope: Scope) => {
      usersScope.action({ type: 'index', element: <UsersPage /> });
    });

    settingsScope.scope({ name: 'companies' }, (companiesScope: Scope) => {
      companiesScope.action({
        type: 'edit',
        element: <EditCompanyPage />,
        member: false,
        optionalExtraParams: ['tab'],
      });
    });

    settingsScope.scope({ name: 'policies' }, (policiesScope: Scope) => {
      policiesScope.action({ type: 'index', element: <PoliciesPage />, optionalExtraParams: ['tab'] });
    });

    settingsScope.scope({ name: 'integrations' }, (integrationsScope: Scope) => {
      integrationsScope.action({ type: 'index', element: <IntegrationsPage /> });
    });
  });
});

/**
 * Routes for users that have root (CRM) access
 */
routes.scope({ element: <RequiredUserRootRoutes /> }, (rootScope: Scope) => {
  rootScope.scope({ name: 'management', element: <ManagementShell /> }, (managementScope: Scope) => {
    managementScope.action({ type: 'index', element: <ManagementIndexRedirect /> });

    managementScope.scope({ name: 'users' }, (usersScope: Scope) => {
      usersScope.action({ type: 'index', element: <CrmUsersPage /> });
      usersScope.action({
        type: 'edit',
        element: <CrmEditUserPage />,
        extraParams: ['companyId'],
        optionalExtraParams: ['tab'],
      });
    });

    managementScope.scope({ name: 'companies' }, (companiesScope: Scope) => {
      companiesScope.action({ type: 'index', element: <CrmCompaniesPage /> });
      companiesScope.action({ type: 'edit', element: <CrmEditCompanyPage />, optionalExtraParams: ['tab'] });
    });

    managementScope.scope({ name: 'agent-conversations' }, (agentConversationsScope: Scope) => {
      agentConversationsScope.action({ type: 'index', element: <CrmAgentConversationsPage /> });
      agentConversationsScope.action({ type: 'show', element: <CrmAgentConversationPage /> });
    });

    managementScope.scope({ name: 'trips' }, (tripsScope: Scope) => {
      tripsScope.action({ type: 'index', element: <CrmTripsPage /> });
      tripsScope.action({ type: 'new', element: <CrmCreateTripsPage /> });
      tripsScope.action({ type: 'edit', element: <CrmEditTripsPage /> });
    });

    managementScope.scope({ name: 'pdf' }, (pdfScope: Scope) => {
      pdfScope.scope({ name: 'trips' }, (tripsPdfScope: Scope) => {
        tripsPdfScope.action({ type: 'show', element: <TripPdfPreview /> });
      });
    });
  });
});
